import React from "react";

const ColorCard = ({ name, hexCode, className = "" }) => {
  return (
    <div className={`rounded-lg overflow-hidden shadow-md w-48 m-2 ${className}`}>
      <div
        className="h-16 w-full"
        style={{ backgroundColor: hexCode }}
      ></div>
      <div className="p-3 bg-white">
        <p className="text-sm font-medium text-gray-700">{name}</p>
        <p className="text-xs text-gray-500">{hexCode}</p>
      </div>
    </div>
  );
};

const ColorSection = ({ title, colors }) => {
  return (
    <div className="mb-8">
      <h2 className="text-xl font-bold mb-3 text-neutral-700">{title}</h2>
      <div className="flex flex-wrap">
        {colors.map((color, index) => (
          <ColorCard key={index} name={color.name} hexCode={color.hexCode} />
        ))}
      </div>
    </div>
  );
};

const ColorPalette = () => {
  // Primary Colors
  const primaryColors = [
    { name: "Primary 500", hexCode: "#113262" },
    { name: "Primary 400", hexCode: "#143970" },
    { name: "Primary 300", hexCode: "#1C52A0" },
    { name: "Primary 200", hexCode: "#436FB0" },
    { name: "Primary 100", hexCode: "#7B9BC8" },
    { name: "Primary 75", hexCode: "#A2B8D8" },
    { name: "Primary 50", hexCode: "#E8EEF6" },
  ];

  // Secondary Colors
  const secondaryColors = [
    { name: "Secondary 500", hexCode: "#955C0A" },
    { name: "Secondary 400", hexCode: "#AB690C" },
    { name: "Secondary 300", hexCode: "#F49611" },
    { name: "Secondary 200", hexCode: "#F6A839" },
    { name: "Secondary 100", hexCode: "#F9C275" },
    { name: "Secondary 75", hexCode: "#FAD49D" },
    { name: "Secondary 50", hexCode: "#FEF5E7" },
  ];

  // Tertiary Colors
  const tertiaryColors = [
    { name: "Desaturated Blue 1", hexCode: "#0C131C" },
    { name: "Desaturated Blue 2", hexCode: "#131C2B" },
  ];

  // Stroke Colors
  const strokeColors = [
    { name: "N 50", hexCode: "#E6E6E6" },
    { name: "N 500", hexCode: "#262626" },
    { name: "N 700", hexCode: "#0D0D0D" },
    { name: "Primary 300", hexCode: "#1C52A0" },
    { name: "Gradient Light", hexCode: "linear-gradient(to right, #1C52A0, #0A1E3A)" },
  ];

  // Neutral Colors
  const neutralColors = [
    { name: "Neutral 900", hexCode: "#000000" },
    { name: "Neutral 800", hexCode: "#050505" },
    { name: "Neutral 700", hexCode: "#0D0D0D" },
    { name: "Neutral 600", hexCode: "#1A1A1A" },
    { name: "Neutral 500", hexCode: "#262626" },
    { name: "Neutral 400", hexCode: "#333333" },
    { name: "Neutral 300", hexCode: "#545454" },
    { name: "Neutral 200", hexCode: "#8A8A8A" },
    { name: "Neutral 100", hexCode: "#B0B0B0" },
    { name: "Neutral 50", hexCode: "#E6E6E6" },
    { name: "Neutral 10", hexCode: "#FFFFFF" },
  ];

  // Semantic Colors - Red
  const redColors = [
    { name: "Red 1000", hexCode: "#42221F" },
    { name: "Red 900", hexCode: "#5D1F1A" },
    { name: "Red 800", hexCode: "#AE2E24" },
    { name: "Red 700", hexCode: "#C9372C" },
    { name: "Red 600 (Warning)", hexCode: "#E2483D" },
    { name: "Red 500", hexCode: "#F15B50" },
    { name: "Red 400", hexCode: "#F87168" },
    { name: "Red 300", hexCode: "#FD9891" },
    { name: "Red 200", hexCode: "#FFD5D2" },
    { name: "Red 100", hexCode: "#FFECEB" },
  ];

  // Semantic Colors - Yellow
  const yellowColors = [
    { name: "Yellow 1000", hexCode: "#332E1B" },
    { name: "Yellow 900", hexCode: "#533F04" },
    { name: "Yellow 800", hexCode: "#7F5F01" },
    { name: "Yellow 700", hexCode: "#946F00" },
    { name: "Yellow 600", hexCode: "#B38600" },
    { name: "Yellow 500 (Warning)", hexCode: "#CF9F02" },
    { name: "Yellow 400", hexCode: "#E2B203" },
    { name: "Yellow 300", hexCode: "#F5CD47" },
    { name: "Yellow 200", hexCode: "#F8E6A0" },
    { name: "Yellow 100", hexCode: "#FFF7D6" },
  ];

  // Semantic Colors - Green
  const greenColors = [
    { name: "Green 1000", hexCode: "#1C3329" },
    { name: "Green 900", hexCode: "#164B35" },
    { name: "Green 800", hexCode: "#216E4E" },
    { name: "Green 700", hexCode: "#1F845A" },
    { name: "Green 600", hexCode: "#22A06B" },
    { name: "Green 500", hexCode: "#2AB87F" },
    { name: "Green 400 (Success)", hexCode: "#4BCE97" },
    { name: "Green 300", hexCode: "#7EE2B8" },
    { name: "Green 200", hexCode: "#BAF3DB" },
    { name: "Green 100", hexCode: "#DCFFF1" },
  ];

  // Semantic Colors - Blue
  const blueColors = [
    { name: "Blue 1000", hexCode: "#1C2B41" },
    { name: "Blue 900", hexCode: "#09326C" },
    { name: "Blue 800", hexCode: "#0055CC" },
    { name: "Blue 700", hexCode: "#0C66E4" },
    { name: "Blue 600", hexCode: "#1D7AFC" },
    { name: "Blue 500", hexCode: "#388BFF" },
    { name: "Blue 400", hexCode: "#579DFF" },
    { name: "Blue 300", hexCode: "#84B8FF" },
    { name: "Blue 200", hexCode: "#CCE0FF" },
    { name: "Blue 100", hexCode: "#E9F2FF" },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-primary-500" style={{ color: "#113262" }}>Maxiom Wealth Color Palette</h1>
      
      <ColorSection title="Primary Colors" colors={primaryColors} />
      <ColorSection title="Secondary Colors" colors={secondaryColors} />
      <ColorSection title="Tertiary Colors" colors={tertiaryColors} />
      <ColorSection title="Stroke Colors" colors={strokeColors} />
      <ColorSection title="Neutral Colors" colors={neutralColors} />
      
      <h2 className="text-xl font-bold mb-3 text-neutral-700">Semantic Colors</h2>
      <ColorSection title="Red" colors={redColors} />
      <ColorSection title="Yellow" colors={yellowColors} />
      <ColorSection title="Green" colors={greenColors} />
      <ColorSection title="Blue" colors={blueColors} />
    </div>
  );
};

export default ColorPalette;